// @ts-nocheck
import React, { useImperativeHandle } from 'react';
import { useLocalization, type Language } from '../../config/localization';
import type { IWorkflowWidget, WidgetPayloadResult, WidgetValidationError } from '../../interfaces/IWorkflowWidget';
import BaseWidget from './BaseWidget';
import Icon, { ICONS } from '../ui/icons';

export interface InputTextWidgetConfig {
  titleRu?: string;
  titleEn?: string;
  placeholderRu?: string;
  placeholderEn?: string;
  outputKey?: string;
  charLimit?: number;            // Default: 5000
  required?: boolean;            // Default: true
}

interface InputTextSmallWidgetProps {
  language: Language;
  prompt: string;
  onPromptChange: (prompt: string) => void;
  config?: InputTextWidgetConfig;
  className?: string;
  charLimit?: number;
  widgetRef?: React.RefObject<IWorkflowWidget>;
}

export default function InputTextSmallWidget({
  language,
  prompt,
  onPromptChange,
  config = {},
  className = '',
  charLimit = 5000,
  widgetRef
}: InputTextSmallWidgetProps) {
  const t = useLocalization(language);
  const effectiveCharLimit = config.charLimit ?? charLimit;

  // Get title based on language, falling back to localization defaults
  const getTitle = (): string => {
    if (language === 'ru' && config.titleRu) {
      return config.titleRu;
    }
    if (language === 'en' && config.titleEn) {
      return config.titleEn;
    }
    // Fallback to localization
    return t.inputText?.title || 'Input Text';
  };

  // Get placeholder based on language, falling back to localization defaults
  const getPlaceholder = (): string => {
    let base: string;
    if (language === 'ru' && config.placeholderRu) {
      base = config.placeholderRu;
    } else if (language === 'en' && config.placeholderEn) {
      base = config.placeholderEn;
    } else {
      base = t.inputText?.placeholder || 'Enter your text here...';
    }
    const hint = (config.required !== false)
      ? (t.inputText?.requiredHint || 'required')
      : (t.inputText?.optionalHint || 'optional');
    return `${base} (${hint})`;
  };

  const outputKey = config.outputKey || 'prompt';
  const isRequired = config.required !== false; // default true

  // IWorkflowWidget implementation via ref
  useImperativeHandle(widgetRef, () => ({
    getPayloadData(): WidgetPayloadResult {
      const trimmedValue = prompt?.trim() || '';
      // Skip empty optional fields from payload
      if (!isRequired && !trimmedValue) {
        return { data: {} };
      }
      return {
        data: {
          [outputKey]: trimmedValue
        }
      };
    },

    validateState(): WidgetValidationError[] {
      const errors: WidgetValidationError[] = [];
      const trimmedPrompt = prompt?.trim() || '';

      if (isRequired && !trimmedPrompt) {
        errors.push({
          field: outputKey,
          message: t.inputText?.errors?.empty || 'Input is required'
        });
      }

      if (trimmedPrompt.length > effectiveCharLimit) {
        errors.push({
          field: outputKey,
          message: t.inputText?.errors?.tooLong || `Input exceeds ${effectiveCharLimit} characters`
        });
      }

      return errors;
    },

    isRequired(): boolean {
      return isRequired;
    }
  }), [prompt, effectiveCharLimit, t, outputKey, isRequired]);

  const charCount = prompt?.length || 0;
  const isOverLimit = charCount > effectiveCharLimit;
  const isNearLimit = charCount > effectiveCharLimit * 0.9;

  return (
    <BaseWidget
      title={getTitle()}
      icon={<Icon name={ICONS.PROMPTS} size={16} className="text-current" />}
      description={t.inputText?.placeholder || 'Enter your text'}
      language={language}
      className={className}
      widgetId="input_text_small"
    >
      <div className="relative">
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={getPlaceholder()}
          rows={4}
          className={`w-full p-3 bg-gray-800/50 border rounded-lg text-white text-sm placeholder-gray-500 resize-none focus:outline-none focus:ring-2 transition-all duration-200 ${
            isOverLimit
              ? 'border-red-500/50 focus:ring-red-500/30 focus:border-red-400'
              : 'border-gray-700 focus:ring-purple-500/30 focus:border-purple-400'
          }`}
        />

        {/* Character counter */}
        <div className={`absolute bottom-2 right-2 text-xs font-mono ${
          isOverLimit
            ? 'text-red-400'
            : isNearLimit
              ? 'text-yellow-400'
              : 'text-gray-500'
        }`}>
          {charCount.toLocaleString()} / {effectiveCharLimit.toLocaleString()}
        </div>
      </div>
    </BaseWidget>
  );
}
